'use client';
import { Button } from '@/shared/ui/Button';
import { Portal } from '@/shared/ui/Portal';
import { twMergeExt } from '@/shared/utils/twMergeExt';
import { AnimatePresence, motion } from 'motion/react';
import { Dispatch, FC, ReactNode, SetStateAction, useEffect } from 'react';
import { useMedia } from 'react-use';
import useMeasure from 'react-use-measure';
import {
  cvaBack,
  cvaBackIcon,
  cvaClose,
  cvaCloseIcon2,
  cvaItem,
  cvaItemContent,
  cvaItemHeader,
  cvaItemHeaderArrow,
  cvaItemHeaderBadge,
  cvaItemHeaderInner,
  cvaItemHeaderTitle,
  cvaItemWrapper,
  cvaMobileFilters,
  cvaMobileFiltersBody,
  cvaMobileFiltersFooter,
  cvaMobileFiltersFooterButton,
  cvaMobileFiltersHeader,
  cvaMobileFiltersHeaderTitle,
  cvaReset,
  cvaResetMobile,
} from './FiltersGroupStyles';
import IconCaret from '/public/icons/arrow-down.svg';
import IconArrowLeft from '/public/icons/arrow-left.svg';
import IconClose2 from '/public/icons/close2.svg';

interface Props {
  i: number;
  header: string;
  expanded: number | boolean;
  setExpanded: Dispatch<SetStateAction<number | false>>;
  children: ReactNode;
  forceShow: boolean;
  onAnimationStart?: () => void;
  onAnimationEnd?: () => void;
  count?: number;
  onReset?: (code: string) => void;
  hasReset?: boolean;
  hasCount?: boolean;
  code: string;
}

const FiltersGroup: FC<Props> = ({
  i,
  header,
  expanded,
  setExpanded,
  forceShow,
  children,
  onAnimationStart,
  onAnimationEnd,
  count,
  hasReset,
  onReset,
  code,
  hasCount,
}) => {
  const isOpen = forceShow || i === expanded;

  const isMobile = useMedia('(max-width: 767px)', true);

  const [filterGroupRef, filterGroupRefBounds] = useMeasure();
  const [filterGroupContentRef, filterGroupContentRefBounds] = useMeasure();
  const [itemWrapperRef, itemWrapperRefBounds] = useMeasure();

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [itemWrapperRefBounds.height]);

  return (
    <div className={cvaItem()} ref={filterGroupRef} data-cursor={'-pointer'}>
      <div
        onClick={() => setExpanded(isOpen ? false : i)}
        className={cvaItemHeader({
          // state: isOpen
          //   ? 'active'
          //   : !isOpen &&
          //       typeof expanded === 'number'
          //     ? 'notActive'
          //     : 'default',
        })}
      >
        <div
          className={cvaItemHeaderInner({
            active: isOpen,
          })}
        >
          <IconCaret
            className={cvaItemHeaderArrow({
              active: isOpen,
              hasReset,
            })}
          />
          <div
            dangerouslySetInnerHTML={{
              __html: header,
            }}
            className={cvaItemHeaderTitle()}
          ></div>
          {hasCount && count ? <div className={cvaItemHeaderBadge()}>{count}</div> : null}
        </div>
        {hasReset && count && count > 0 ? (
          <div className={cvaReset()} onClick={() => onReset?.(code)}>
            Сбросить
          </div>
        ) : null}
      </div>

      <AnimatePresence initial={false}>
        {isOpen && !isMobile ? (
          <motion.div
            ref={itemWrapperRef}
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: {
                opacity: 1,
                height: filterGroupContentRefBounds.height,
              },
              collapsed: {
                opacity: 0,
                height: 0,
              },
            }}
            transition={{ duration: 0.3 }}
            className={twMergeExt(cvaItemWrapper())}
            onAnimationStart={onAnimationStart}
            onAnimationComplete={(variant) => {
              onAnimationEnd?.();
              if (filterGroupRefBounds.top < 0 && variant === 'open') {
                const scrollOffset = filterGroupRefBounds.top + +scrollY;

                window.scroll({
                  top: scrollOffset,
                  behavior: 'smooth',
                });
              }
            }}
          >
            <motion.div
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1 },
                collapsed: { opacity: 0 },
              }}
              transition={{ duration: 0.3 }}
              className={twMergeExt(cvaItemContent(), 'nojs-opacity1')}
              ref={filterGroupContentRef}
            >
              {children}
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
      <AnimatePresence>
        {isOpen && isMobile ? (
          <Portal containerId="popup-filters">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: 'easeInOut',
              }}
              className={cvaMobileFilters()}
            >
              <div className={cvaMobileFiltersHeader()}>
                <button type="button" className={cvaBack()} onClick={() => setExpanded(false)}>
                  <IconArrowLeft className={cvaBackIcon()} />
                </button>
                <div
                  className={cvaMobileFiltersHeaderTitle()}
                  dangerouslySetInnerHTML={{
                    __html: header,
                  }}
                ></div>
                {hasReset ? (
                  <div className={twMergeExt(cvaReset(), cvaResetMobile())} onClick={() => onReset?.(code)}>
                    Сбросить
                  </div>
                ) : (
                  <button type="button" className={cvaClose()} onClick={() => setExpanded(false)}>
                    <IconClose2 className={cvaCloseIcon2()} />
                  </button>
                )}
              </div>
              <div className={cvaMobileFiltersBody()}>{children}</div>
              <div className={cvaMobileFiltersFooter()}>
                <Button
                  type={'button'}
                  color={'mediumGreen'}
                  size={'medium'}
                  className={cvaMobileFiltersFooterButton()}
                  rootWrapperClassName="w-full"
                  animateWrapperClassName="w-full"
                  onClick={() => setExpanded(false)}
                >
                  Применить
                </Button>
              </div>
            </motion.div>
          </Portal>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default FiltersGroup;
